import { Settings, Theme } from "../types";

const settings: Settings = {
    weather: {
        id: "weather",
        name: "Weather",
        key: "weather",
        detail: "",
        units: "",
        categories: [],
        comparisons: [],
        subfields: ["temperature", "humidity", "precipitation", "wind"],
        period: 0,
        isOutOfRange(value: number | string) {
            return false;
        },
        scale: () => ({
            start: 0,
            values: [],
        }),
    },
    airQuality: {
        id: "air-quality",
        name: "Air Quality",
        key: "airQuality",
        detail: "",
        units: "",
        categories: [],
        comparisons: [],
        subfields: ["fineParticles", "coarseParticles", "ozone", "nitrogenDioxide"],
        period: 0,
        isOutOfRange(value: number | string) {
            return value === "OUT_OF_RANGE";
        },
        scale: (theme: Theme) => ({
            start: 0,
            values: [
                { stop: 33, color: theme.color.blue1, label: "Very good", description: "Very good" },
                { stop: 67, color: theme.color.green1, label: "Good", description: "Good" },
                { stop: 100, color: theme.color.yellow1, label: "Fair", description: "Fair" },
                { stop: 150, color: theme.color.orange1, label: "Poor", description: "Poor" },
                { stop: 200, color: theme.color.red1, label: "Very poor", description: "Very poor" },
                { stop: 250, color: theme.color.maroon1, label: "Hazardous", description: "Hazardous" },
            ],
        }),
    },
    temperature: {
        id: "temperature",
        name: "Temperature",
        key: "temperature",
        parentField: "weather",
        detail: "is a measure of how hot or cold it is outside.",
        units: "°C",
        categories: ["Cold", "Warm", "Hot"],
        comparisons: ["Colder than", "Similar to", "Hotter than"],
        period: 0,
        isOutOfRange: (value: number | string) => value < -10 || value > 50,
        scale: (theme: Theme) => ({
            start: -5,
            values: [
                { stop: 0, color: theme.color.blue5, description: "Cold day" },
                { stop: 5, color: theme.color.blue4, description: "Cold day" },
                { stop: 10, color: theme.color.blue2, description: "Cold day" },
                { stop: 15, color: theme.color.blue1, description: "Cool day" },
                { stop: 20, color: theme.color.yellow1, description: "Cool day" },
                { stop: 25, color: theme.color.orange1, description: "Warm day" },
                { stop: 30, color: theme.color.orangered1, description: "Warm day" },
                { stop: 35, color: theme.color.red1, description: "Hot day" },
                { stop: 40, color: theme.color.red2, description: "Hot day" },
                { stop: 45, color: theme.color.red3, description: "Hot day" },
                { stop: 50, color: theme.color.red4, description: "Hot day" },
            ],
        }),
    },
    humidity: {
        id: "humidity",
        name: "Humidity",
        key: "humidity",
        parentField: "weather",
        isOutOfRange: (value: number | string) => value < 0 || value > 100,
        detail: "is the amount of moisture in the air causing the body to sweat.",
        units: "%",
        categories: ["Dry", "Comfortable", "Saturated"],
        comparisons: ["Less humid than", "Similar to", "More humid than"],
        period: 0,
        scale: (theme: Theme) => ({
            start: 0,
            values: [
                { stop: 20, color: theme.color.blue1, description: "Dry day" },
                { stop: 40, color: theme.color.blue2, description: "Comfortable day" },
                { stop: 60, color: theme.color.blue3, description: "Saturated" },
                { stop: 80, color: theme.color.blue4, description: "Saturated" },
                { stop: 100, color: theme.color.blue5, description: "Saturated" },
            ],
        }),
    },
    precipitation: {
        id: "precipitation",
        name: "Precipitation",
        key: "precipitation",
        parentField: "weather",
        detail: "is number | string type of water that forms in the atmosphere and drops to the surface.",
        units: "mm",
        isOutOfRange: (value: number | string) => value < 0 || value > 200,
        categories: ["Drizzle", "Light", "Heavy"],
        comparisons: ["Less rain than", "Similar to", "More rain than"],
        period: 1,
        scale: (theme: Theme) => ({
            start: 0,
            values: [
                { stop: 1, color: theme.color.green1, description: "No rain" },
                { stop: 10, color: theme.color.green1, description: "Drizzle of rain" },
                { stop: 20, color: theme.color.green2, description: "Light rain" },
                { stop: 30, color: theme.color.green3, description: "Light rain" },
                { stop: 40, color: theme.color.green4, description: "Heavy rain" },
                { stop: 50, color: theme.color.green5, description: "Heavy rain" },
            ],
        }),
    },
    wind: {
        id: "wind",
        name: "Wind",
        isOutOfRange: (value: number | string) => value < 0 || value > 200,
        key: "windSpeedKmH",
        parentField: "weather",
        detail: "is the movement of air across the Earth's surface.",
        extra: "windDirection",
        units: "km/h",
        categories: ["Calm", "Strong", "Severe"],
        comparisons: ["Less wind than", "Similar to", "More wind than"],
        period: 0,
        scale: (theme: Theme) => ({
            start: 0,
            values: [
                { stop: 2, color: theme.color.magenta1, description: "Calm winds" },
                { stop: 12, color: theme.color.magenta2, description: "Light winds" },
                { stop: 39, color: theme.color.magenta3, description: "Moderate winds" },
                { stop: 75, color: theme.color.magenta4, description: "Strong winds" },
                { stop: 80, color: theme.color.magenta5, description: "Severe winds" },
            ],
        }),
    },
    fineParticles: {
        id: "fine-particles",
        name: "Particles less than 2.5μm",
        shortName: "PM2.5",
        isOutOfRange: (value: number | string) => value < 0 || value > 1000,
        key: "fineParticles",
        parentField: "airQuality",
        detail: "",
        units: "μg/m³",
        categories: [],
        comparisons: [],
        standard: 25,
        period: 24,
        scale: () => ({
            start: 0,
            values: [],
        }),
    },
    coarseParticles: {
        id: "coarse-particles",
        name: "Particles less than 10μm",
        shortName: "PM10",
        key: "coarseParticles",
        parentField: "airQuality",
        isOutOfRange: (value: number | string) => value < 0 || value > 1000,
        detail: "",
        categories: [],
        comparisons: [],
        units: "μg/m³",
        standard: 50,
        period: 24,
        scale: () => ({
            start: 0,
            values: [],
        }),
    },
    ozone: {
        id: "ozone",
        name: "Ozone",
        shortName: "O3",
        key: "ozone",
        parentField: "airQuality",
        detail: "",
        categories: [],
        comparisons: [],
        isOutOfRange: (value: number | string) => value < -0.5 || value > 100,
        units: "pphm",
        longUnits: "parts per hundred million",
        standard: 10,
        period: 1,
        decimals: 2,
        scale: () => ({
            start: 0,
            values: [],
        }),
    },
    nitrogenDioxide: {
        id: "nitrogenDioxide",
        name: "Nitrogen dioxide",
        shortName: "NO2",
        key: "nitrogenDioxide",
        parentField: "airQuality",
        detail: "",
        isOutOfRange: (value: number | string) => value < -0.5 || value > 100,
        categories: [],
        comparisons: [],
        units: "pphm",
        longUnits: "parts per hundred million",
        standard: 12,
        period: 1,
        decimals: 2,
        scale: () => ({
            start: 0,
            values: [],
        }),
    },
    carbonMonoxide: {
        id: "carbon-monoxide",
        name: "Carbon Monoxide",
        key: "carbonMonoxide",
        parentField: "airQuality",
        detail: "",
        categories: [],
        comparisons: [],
        units: "parts per million",
        standard: 9,
        period: 8,
        isOutOfRange(value: number | string) {
            return false;
        },
        scale: () => ({
            start: 0,
            values: [],
        }),
    },
    carbonDioxide: {
        id: "carbon-dioxide",
        name: "carbonDioxide",
        key: "carbonDioxide",
        parentField: "airQuality",
        detail: "",
        categories: [],
        comparisons: [],
        units: "parts per million",
        standard: 9,
        period: 8,
        isOutOfRange(value: number | string) {
            return false;
        },
        scale: () => ({
            start: 0,
            values: [],
        }),
    },
    sulfurDioxide: {
        id: "sulfur-dioxide",
        name: "Sulfur Dioxide",
        key: "sulfurDioxide",
        parentField: "airQuality",
        detail: "",
        categories: [],
        comparisons: [],
        units: "parts per million",
        standard: 0.2,
        period: 1,
        isOutOfRange(value: number | string) {
            return false;
        },
        scale: () => ({
            start: 0,
            values: [],
        }),
    },
};

export default settings;
